import { AnonymousListItem } from "@/components/employee/AnonymousListItem.tsx";
import { FormParticipantStatusTag } from "@/components/forms/components/FormParticipantStatusTag.tsx";
import {
  useModals,
  Modal,
  ModalProps,
  Box,
  Button,
  Checkbox,
  ListItem,
  Stack,
  Switch,
  Tag,
  Title,
} from "@skailer-com/uikit";
import { ScoreTag } from "@/pages/talent/performance-reviews/view/components/ScoreTag.tsx";
import { useParticipantShareSettingUpdate } from "@/queries/performance-reviews/use-performance-reviews.query.ts";
import { ApiEmployee } from "@/types/employees/employee.types.ts";
import { ApiFormParticipantStatus } from "@/types/forms/form.types.ts";
import {
  ApiPerformanceReviewParticipantShareSetting,
  ApiPerformanceReviewParticipantShareSettingUpdate,
  PerformanceReviewReviewerTypeItems,
} from "@/types/performance-review.types.ts";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

export interface PerformanceReviewParticipantShareSettingModal extends ModalProps {
  performanceReviewId: number;
  employee: ApiEmployee;
  shareSetting: ApiPerformanceReviewParticipantShareSetting;
}

const PerformanceReviewParticipantShareSettingUpdateSchema = yup.object({
  is_shared: yup.boolean().required(),
  is_share_all_feedback: yup.boolean().required(),
  share_respondent_uuids: yup.array().of(yup.string().required()).required(),
});

export function PerformanceReviewParticipantShareSettingModal({
  performanceReviewId,
  employee,
  shareSetting,
  ...props
}: PerformanceReviewParticipantShareSettingModal) {
  const { t } = useTranslation();
  const { id } = { ...props };
  const { close } = useModals();

  const updateShareSetting = useParticipantShareSettingUpdate();

  const allReviewers = shareSetting.reviewer_type_share_settings.flatMap(
    (type) => type.reviewers_share_settings
  );

  const { control, watch, handleSubmit } =
    useForm<ApiPerformanceReviewParticipantShareSettingUpdate>({
      mode: "onSubmit",
      resolver: yupResolver(PerformanceReviewParticipantShareSettingUpdateSchema),
      defaultValues: {
        is_shared: shareSetting.is_shared,
        is_share_all_feedback: shareSetting.is_shared
          ? allReviewers.every((reviewer) => reviewer.is_feedback_shared)
          : true,
        share_respondent_uuids: allReviewers
          .filter((reviewer) => (shareSetting.is_shared ? reviewer.is_feedback_shared : true))
          .map((reviewer) => reviewer.respondent_uuid),
      },
    });

  const isShared = watch("is_shared");
  const isShareAllFeedback = watch("is_share_all_feedback");

  const handleSubmitStartOrUpdate = handleSubmit(async (data) => {
    try {
      await updateShareSetting.mutateAsync({
        id: performanceReviewId,
        employee_id: employee.id,
        payload: {
          is_shared: true,
          is_share_all_feedback: data.is_share_all_feedback,
          share_respondent_uuids: data.is_share_all_feedback ? [] : data.share_respondent_uuids,
        },
      });
      close(id);
    } catch {}
  });

  const handleSubmitStop = handleSubmit(async () => {
    try {
      await updateShareSetting.mutateAsync({
        id: performanceReviewId,
        employee_id: employee.id,
        payload: {
          is_shared: false,
          is_share_all_feedback: false,
          share_respondent_uuids: [],
        },
      });
      close(id);
    } catch {}
  });

  return (
    <Modal
      title={t("performance_review:share_feedbacks")}
      variant={"middle"}
      layout={"base"}
      withCloser={true}
      {...props}
      actions={
        <Button
          size={"lg"}
          className={"w-full"}
          isLoading={updateShareSetting.isPending}
          onClick={handleSubmitStartOrUpdate}
        >
          {isShared
            ? t("common:save")
            : t("performance_review:start_share_with", { name: employee.first_name })}
        </Button>
      }
    >
      {isShared ? (
        <Box className={"pb-6"}>
          <Button
            size={"lg"}
            className={"w-full"}
            variant={"secondary"}
            isLoading={updateShareSetting.isPending}
            onClick={handleSubmitStop}
          >
            {t("performance_review:stop_sharing_with", { name: employee.first_name })}
          </Button>
        </Box>
      ) : (
        <></>
      )}

      <Controller
        control={control}
        name={"is_share_all_feedback"}
        render={({ field }) => (
          <Switch
            title={t("performance_review:share_all_feedback_title")}
            description={t("performance_review:share_all_feedback_description", {
              name: employee.first_name,
            })}
            checked={field.value}
            onChange={() => field.onChange(!field.value)}
          />
        )}
      />
      {!isShareAllFeedback ? (
        <Stack gap={"lg"} className={"pl-12 pt-4"}>
          <Title
            header={t("performance_review:share_all_feedback_select_title", {
              name: employee.first_name,
            })}
            size={"xs"}
            className={"text-title"}
          />

          {shareSetting.reviewer_type_share_settings.map((type) => {
            if (type.reviewers_share_settings.length == 0) return;
            const reviewer_type = type.reviewer_type;
            const reviewers = type.reviewers_share_settings;

            return (
              <Box key={`reviewer-type-${reviewer_type.id}`}>
                <Title
                  size={"xs"}
                  header={
                    PerformanceReviewReviewerTypeItems().find(
                      (item) => item.id == reviewer_type.type
                    )?.shortTitle ??
                    reviewer_type.title ??
                    ""
                  }
                />
                <Controller
                  control={control}
                  name="share_respondent_uuids"
                  render={({ field }) => {
                    const shareReviewerIds = field.value ?? [];

                    const handleToggle = (respondentUuid: string) => {
                      const currentIds = [...shareReviewerIds];
                      const index = currentIds.indexOf(respondentUuid);

                      if (index > -1) currentIds.splice(index, 1);
                      else currentIds.push(respondentUuid);

                      field.onChange(currentIds);
                    };

                    return (
                      <Stack>
                        {reviewers.map((reviewer) => {
                          const isChecked = shareReviewerIds.includes(reviewer.respondent_uuid);
                          return (
                            <ListItem
                              key={`reviewer-${reviewer.respondent_uuid}`}
                              className="cursor-pointer"
                              onClick={() => handleToggle(reviewer.respondent_uuid)}
                              titleSlot={
                                <Stack gap="xs" direction="horizontal" items="center">
                                  <Checkbox
                                    checked={isChecked}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={() => {}}
                                  />
                                  <AnonymousListItem
                                    employee={reviewer.employee}
                                    behavior={"card"}
                                    uuid={reviewer.respondent_uuid}
                                  />
                                </Stack>
                              }
                              valueSlot={
                                <Stack direction={"horizontal"}>
                                  {reviewer.status ? (
                                    <>
                                      {reviewer.status === ApiFormParticipantStatus.completed &&
                                      !type.is_below_anonymity_threshold ? (
                                        <ScoreTag score={reviewer.scale_score.avg_scale_score} />
                                      ) : (
                                        <FormParticipantStatusTag status={reviewer.status} />
                                      )}
                                    </>
                                  ) : (
                                    <Tag>{t("common:anonymous")}</Tag>
                                  )}
                                </Stack>
                              }
                            />
                          );
                        })}
                      </Stack>
                    );
                  }}
                />
              </Box>
            );
          })}
        </Stack>
      ) : (
        <></>
      )}
    </Modal>
  );
}
